/**
 * Único lugar donde se resuelven las URLs de los dos backends.
 *
 * Antes cada consumidor las resolvía por su cuenta y el hostname de Render
 * aparecía escrito a mano en tres módulos distintos (`useAppApi.js`,
 * `AdminRoute.js` y, con el host equivocado, `api/og.js`). Eso es lo
 * que ataba el repositorio a un proveedor concreto: cambiar de host obligaba a
 * tocar código y a acertar en todos los sitios a la vez. Ver
 * `docs/DESLIGAR-DE-RENDER.md`.
 *
 * Ahora el host se decide **solo** con variables de entorno de Vercel:
 *
 *   REACT_APP_BACKEND_URL      → backend de la web
 *   REACT_APP_APP_BACKEND_URL  → backend de la app
 *
 * Los respaldos de abajo son la última red de seguridad por si una variable
 * llega vacía o corrupta en runtime (ha pasado: «backend URL inválida» en
 * producción y en preview). No son la fuente de la verdad — esa es el panel del
 * proveedor.
 */

// Dominios propios, verificados en Render el 8/08/2026. Son nuestros: apuntan
// hoy a Render mediante un CNAME en IONOS, y el día que se cambie de proveedor
// se reapunta ese CNAME sin tocar una línea de código ni volver a desplegar.
//
// Por eso los respaldos ya NO son hostnames de Render: escribir aquí el nombre
// de un proveedor es exactamente lo que ataba el repositorio. Ver
// `docs/DESLIGAR-DE-RENDER.md`.
const RESPALDO_WEB = "https://api.evangelizaciondigitalhuelva.org";
const RESPALDO_APP = "https://api-app.evangelizaciondigitalhuelva.org";

/**
 * Normaliza el valor de una variable de entorno a una base de URL usable, o
 * devuelve el respaldo si no lo es. Tolera lo que se ha visto llegar de verdad:
 * espacios, comillas, saltos de línea, barras finales, falta de esquema y
 * plantillas sin expandir del tipo `${{backend.RAILWAY_URL}}`.
 */
export function normalizarBase(valor, respaldo) {
  if (!valor) return respaldo;

  let v = String(valor)
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .trim()
    .replace(/\/+$/, "");

  if (!v) return respaldo;
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`;

  try {
    const u = new URL(v);
    // Un hostname con forma de dominio real. Descarta plantillas sin expandir,
    // espacios y demás basura.
    if (!u.hostname || !u.hostname.includes(".")) return respaldo;
    if (!/^[a-z0-9.-]+$/i.test(u.hostname)) return respaldo;
    return `${u.protocol}//${u.host}`;
  } catch {
    return respaldo;
  }
}

/** Base del backend de la web, sin barra final y sin `/api`. */
export const WEB_BACKEND = normalizarBase(
  process.env.REACT_APP_BACKEND_URL,
  RESPALDO_WEB
);

/** Base del backend de la app, sin barra final y sin `/api`. */
export const APP_BACKEND = normalizarBase(
  process.env.REACT_APP_APP_BACKEND_URL,
  RESPALDO_APP
);
